import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Department } from '@/types/departments';
import { useToast } from './useToast';
import { useNavigate } from 'react-router-dom';

export const useDepartment = (id: string) => {
  const [department, setDepartment] = useState<Department | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetchDepartment = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('departments')
          .select(`
            *,
            manager:manager_id (
              id,
              full_name,
              email,
              role
            )
          `)
          .eq('id', id)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          if (isMounted) {
            setError('Department not found');
            setDepartment(null);
          }
          return;
        }

        if (isMounted) {
          setDepartment(data);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching department:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch department');
          setDepartment(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchDepartment();

    return () => {
      isMounted = false;
    };
  }, [id]);

  return { department, isLoading, error };
};